import { useEffect, useRef, useState } from "react";
import Plot from "react-plotly.js";
import { geometryApi } from "../../api/blades";

import { useChartColors } from "../../chartTheme";
const DEG2RAD = Math.PI / 180;

// Dash cycle for stream surface lines within a design
const DASHES = ["solid", "dash", "dot", "dashdot"];

export default function DesignComparisonPanel({ designIds, mecoDesigns }) {
  const { DESIGN_COLORS } = useChartColors();
  const [viewData, setViewData] = useState({});
  const [loadingCount, setLoadingCount] = useState(0);
  const fetchedRef = useRef(new Set());

  const idKey = [...designIds].sort().join(",");

  useEffect(() => {
    [...designIds].forEach(id => {
      if (fetchedRef.current.has(id)) return;
      fetchedRef.current.add(id);
      setLoadingCount(n => n + 1);
      geometryApi.bladeViews(id)
        .then(({ data }) => setViewData(prev => ({ ...prev, [id]: data })))
        .catch(() => { fetchedRef.current.delete(id); })
        .finally(() => setLoadingCount(n => n - 1));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey]);

  const ids = [...designIds];
  const hasWrap = ids.some(id => viewData[id]?.wrap?.length);

  // ── MECO traces ──────────────────────────────────────────────────────────────
  const mecoTraces = [];
  ids.forEach((id, di) => {
    const design = mecoDesigns[id];
    const data = viewData[id];
    const color = DESIGN_COLORS[di % DESIGN_COLORS.length];
    const dname = design?.name || `Design ${di + 1}`;

    if (data?.hub_shroud?.length) {
      data.hub_shroud.forEach((l, li) => {
        mecoTraces.push({
          type: "scatter", mode: "lines",
          name: li === 0 ? dname : undefined,
          showlegend: li === 0,
          legendgroup: String(id),
          x: l.r, y: l.z,
          line: { color, width: 1.8, dash: l.name === "SHROUD" ? "dash" : "solid" },
        });
      });
    } else if (design?.construction_lines) {
      // Fallback to raw MECO control points while geometry is loading
      let first = true;
      design.construction_lines
        .filter(cl => cl.name === "HUB" || cl.name === "SHROUD")
        .forEach(cl => {
          if (!cl.r_coords || !cl.z_coords) return;
          mecoTraces.push({
            type: "scatter", mode: "lines",
            name: first ? dname : undefined,
            showlegend: first,
            legendgroup: String(id),
            x: cl.r_coords, y: cl.z_coords,
            line: { color, width: 1.8, dash: cl.name === "SHROUD" ? "dash" : "solid" },
          });
          first = false;
        });
    }
  });

  // ── Wrap traces ───────────────────────────────────────────────────────────────
  const wrapTraces = [];
  ids.forEach((id, di) => {
    const data = viewData[id];
    const design = mecoDesigns[id];
    if (!data?.wrap?.length) return;
    const color = DESIGN_COLORS[di % DESIGN_COLORS.length];
    const dname = design?.name || `Design ${di + 1}`;

    data.wrap.forEach((l, li) => {
      wrapTraces.push({
        type: "scatter", mode: "lines",
        name: li === 0 ? dname : undefined,
        showlegend: li === 0,
        legendgroup: String(id),
        x: l.theta_deg.map((t, j) => l.r[j] * Math.cos(t * DEG2RAD)),
        y: l.theta_deg.map((t, j) => l.r[j] * Math.sin(t * DEG2RAD)),
        line: { color, width: 1.5, shape: "spline", dash: DASHES[li % DASHES.length] },
      });
    });
  });

  const axisBase = {
    gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af",
    tickfont: { size: 10 }, zeroline: false,
    showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot",
  };
  const commonLayout = {
    paper_bgcolor: "transparent",
    plot_bgcolor: "#f9fafb",
    showlegend: true,
    legend: { font: { size: 9 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
    margin: { l: 50, r: 14, t: 8, b: 40 },
    hovermode: "closest",
    dragmode: "pan",
  };
  const commonConfig = {
    displayModeBar: "hover",
    modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]],
    scrollZoom: true,
    responsive: true,
    displaylogo: false,
  };

  return (
    <div className="relative" style={{ height: 360 }}>
      {loadingCount > 0 && (
        <span className="absolute top-0 right-0 text-xs text-bh-500 z-10">Computing…</span>
      )}
      <div className={`grid gap-4 h-full ${hasWrap ? "grid-cols-2" : "grid-cols-1"}`}>
        {/* Meridional Contour */}
        <div className="flex flex-col h-full">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
            Meridional Contour
          </p>
          <div className="flex-1 min-h-0">
            <Plot
              data={mecoTraces}
              layout={{
                ...commonLayout,
                xaxis: { ...axisBase, title: { text: "r [mm]", font: { size: 10 } } },
                yaxis: { ...axisBase, title: { text: "z [mm]", font: { size: 10 } }, scaleanchor: "x", scaleratio: 1 },
              }}
              config={commonConfig}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler
            />
          </div>
        </div>

        {/* Wrap */}
        {hasWrap && (
          <div className="flex flex-col h-full">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
              Wrap
            </p>
            <div className="flex-1 min-h-0">
              <Plot
                data={wrapTraces}
                layout={{
                  ...commonLayout,
                  xaxis: { ...axisBase, title: { text: "x [mm]", font: { size: 10 } } },
                  yaxis: { ...axisBase, title: { text: "y [mm]", font: { size: 10 } }, scaleanchor: "x", scaleratio: 1 },
                }}
                config={commonConfig}
                style={{ width: "100%", height: "100%" }}
                useResizeHandler
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
